import React from 'react';
import {Box, Chip, CircularProgress, Paper, Typography} from '@mui/material';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {useGetComsDailyReportsQuery, ComsDailyReport} from '../../redux/comsApi';

interface Props {
  blockId: string;
}

const COLOR = '#bf8c00';
const fmt = (v: number) =>
  '$' +
  (v ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const EquipmentSummary: React.FC<Props> = ({blockId}) => {
  const {data: reports = [], isLoading} = useGetComsDailyReportsQuery(blockId);

  const map: Record<string, {name: string; unit: string; totalHours: number; totalAmount: number}> = {};
  for (const r of reports as ComsDailyReport[]) {
    for (const e of (r.equipment ?? []) as any[]) {
      const key = e.catalogItemId ?? e.name;
      if (!map[key]) map[key] = {name: e.name, unit: e.unit, totalHours: 0, totalAmount: 0};
      map[key].totalHours  += Number(e.hours)  || 0;
      map[key].totalAmount += Number(e.amount) || 0;
    }
  }
  const rows = Object.values(map).map((v, i) => ({id: i, ...v}));
  const totalEquipment = rows.reduce(
    (sum, row) => sum + (Number(row.totalAmount) || 0),
    0,
  );

  const columns: GridColDef[] = [
    {field: 'name',        headerName: 'Equipment',        flex: 1},
    {field: 'unit',        headerName: 'Unit',             width: 90},
    {field: 'totalHours',  headerName: 'Total Hours',      width: 140, type: 'number'},
    {field: 'totalAmount', headerName: 'Total Amount ($)', width: 160, type: 'number', valueFormatter: (v: any) => `$${(v ?? 0).toFixed(2)}`},
  ];

  return (
    <Box>
      <Typography variant="h2" sx={{color: COLOR, fontWeight: 600, mb: 3}}>
        Equipment — Cumulative Usage
      </Typography>
      {!isLoading && (
        <Box display="flex" flexWrap="wrap" gap={1.5} mb={2}>
          <Chip
            label={`Recorded Equipment Cost: ${fmt(totalEquipment)}`}
            sx={{bgcolor: '#fff3e0', color: COLOR, fontWeight: 700}}
          />
          <Chip
            label={`Entries: ${rows.length}`}
            variant="outlined"
            sx={{borderColor: '#ffcc80', color: COLOR}}
          />
        </Box>
      )}
      <Paper sx={{borderRadius: 2}}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            hideFooterSelectedRowCount
            sx={{border: 0, '& .MuiDataGrid-columnHeaders': {bgcolor: COLOR, color: '#fff'}}}
          />
        )}
      </Paper>
    </Box>
  );
};

export default EquipmentSummary;
